/**
 * DOM port of amgcommon_v2 GameResultPanel — used for StartBet / NoMoreBet /
 * Win / Lose centred banner messages.
 *
 * Each panel is its own element instance (matches the Flutter pattern with
 * three GlobalKeys: _startBetPanelKey / _noMoreBetPanelKey / _winLosePanelKey
 * in game_screen.dart:75-77).
 *
 * Auto-hide after `durationMs` unless `persistent` is true. Calls `onHide`
 * exactly once per show/hide cycle when the panel disappears.
 */

export type PanelType = 'startBet' | 'noMoreBet' | 'win' | 'lose';

interface PanelTemplate {
  cssClass: PanelType;
  title: string;
  durationMs: number;
}

const TEMPLATES: Record<PanelType, PanelTemplate> = {
  startBet:  { cssClass: 'startBet',  title: 'PLACE YOUR BETS',  durationMs: 2200 },
  noMoreBet: { cssClass: 'noMoreBet', title: 'NO MORE BETS',     durationMs: 2200 },
  win:       { cssClass: 'win',       title: 'YOU WIN',          durationMs: 3500 },
  lose:      { cssClass: 'lose',      title: 'YOU LOSE',         durationMs: 3500 },
};

function fmtCurrency(n: number): string {
  return Math.round(n).toLocaleString('en-US');
}

export class GameResultPanel {
  private readonly el: HTMLElement;
  private readonly titleEl: HTMLElement;
  private readonly amountEl: HTMLElement;
  private readonly type: PanelType;
  private hideTimer: number | null = null;
  private currentOnHide: (() => void) | null = null;

  constructor(type: PanelType) {
    this.type = type;
    const host = document.getElementById('dom-hud');
    if (!host) throw new Error('GameResultPanel: #dom-hud missing');

    const tpl = TEMPLATES[type];
    this.el = document.createElement('div');
    this.el.className = `game-result-panel ${tpl.cssClass}`;

    this.titleEl = document.createElement('div');
    this.titleEl.className = 'grp-title';
    this.titleEl.textContent = tpl.title;

    this.amountEl = document.createElement('div');
    this.amountEl.className = 'grp-amount';
    this.amountEl.style.display = 'none';

    this.el.append(this.titleEl, this.amountEl);
    host.appendChild(this.el);
  }

  /**
   * Show the panel. If `amount` is provided, displays it below the title
   * (used by win/lose). `onHide` fires once when this show cycle ends.
   */
  show(opts: { amount?: number; onHide?: () => void } = {}): void {
    if (this.hideTimer != null) {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    if (opts.amount != null) {
      this.amountEl.style.display = '';
      this.amountEl.textContent = fmtCurrency(opts.amount);
    } else {
      this.amountEl.style.display = 'none';
      this.amountEl.textContent = '';
    }
    this.currentOnHide = opts.onHide ?? null;
    this.el.classList.add('visible');

    const tpl = TEMPLATES[this.type];
    this.hideTimer = window.setTimeout(() => this.hide(), tpl.durationMs);
  }

  hide(): void {
    if (this.hideTimer != null) {
      window.clearTimeout(this.hideTimer);
      this.hideTimer = null;
    }
    if (!this.el.classList.contains('visible')) return;
    this.el.classList.remove('visible');
    const cb = this.currentOnHide;
    this.currentOnHide = null;
    if (cb) cb();
  }

  isVisible(): boolean {
    return this.el.classList.contains('visible');
  }
}
